import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { StorageUtility } from "@/lib/storage";
import { cn, currentEnvironment } from "@/lib/utils";
import { AlertTriangle, ShieldAlert, LogOut, CreditCard } from "lucide-react";
import { datadogRum } from "@datadog/browser-rum";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { CurrentEnvironment } from "@/components/current-environment";
import { SessionExpirationCheck } from "@/routes/auth/_auth-layout/-components/auth/session-expiration-check";
import { jwtIsExpired, parseVolumeJwt, redirectToSignIn } from "@/lib/auth";

const validateAdminSession = (token: string) => {
  if (jwtIsExpired(token)) {
    throw redirectToSignIn();
  }

  const tokenContents = parseVolumeJwt(token);
  const adminEmail = StorageUtility.getItem("v_adminEmail_v1");

  if (adminEmail) {
    datadogRum.setGlobalContextProperty("adminEmail", adminEmail);
  }

  if (!tokenContents.adminId && tokenContents.merchantId) {
    throw redirect({
      to: "/payments/payin",
    });
  }

  if (!tokenContents.adminId || tokenContents.merchantId) {
    throw redirectToSignIn();
  }

  return {
    tokenContents,
    adminEmail,
  };
};

export const Route = createFileRoute("/(app)/_admin-layout")({
  component: RouteComponent,
  beforeLoad: async () => {
    datadogRum.setGlobalContextProperty("environment", currentEnvironment());

    const token = StorageUtility.getItem("v_accessToken_v1");
    if (!token) {
      throw redirectToSignIn();
    }

    const { tokenContents, adminEmail } = validateAdminSession(token);

    return {
      accessToken: token,
      decodedJwt: tokenContents,
      authorities: tokenContents.authorities,
      adminEmail: adminEmail ?? "",
      isAdmin: tokenContents.adminId !== undefined,
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const router = useRouter();

  return (
    <SidebarProvider>
      <SessionExpirationCheck decodedJwt={context.decodedJwt} />
      <Sidebar>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>
              <div className="m-auto flex h-20 items-center justify-center">
                <img
                  src="/volume-logo.svg"
                  width={150}
                  height={40}
                  alt="Volume Logo"
                />
              </div>
            </SidebarGroupLabel>
            <SidebarGroupContent className="mt-[60px]">
              <CurrentEnvironment />
              <SidebarMenu>
                <SidebarMenuItem className="mx-3 my-1">
                  <SidebarMenuButton asChild>
                    <Link
                      to="/admin"
                      activeOptions={{
                        includeSearch: false,
                        exact: true,
                      }}
                      className="flex cursor-pointer items-center rounded-lg p-6 v-subtitle-2"
                      activeProps={{
                        className: "bg-V_Secondary20 v-subtitle-2-V_Main",
                      }}
                    >
                      <ShieldAlert />
                      <span>Admin Panel</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem className="mx-3 my-1">
                  <SidebarMenuButton asChild>
                    <Link
                      to="/admin/payments"
                      activeOptions={{
                        includeSearch: false,
                      }}
                      className="flex cursor-pointer items-center rounded-lg p-6 v-subtitle-2"
                      activeProps={{
                        className: "bg-V_Secondary20 v-subtitle-2-V_Main",
                      }}
                    >
                      <CreditCard />
                      <span>Payments</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      <main
        className="bg-volume-grey w-full overflow-y-auto"
        style={{
          scrollbarGutter: "stable",
          width: "max(calc(100% - var(--sidebar-width)), 750px)",
          height: "100vh",
        }}
      >
        <div className="my-4 flex flex-1 flex-col pl-8 pr-4 ">
          <div className="flex items-center w-full">
            <div className={cn("flex items-center mb-4 w-full")}>
              <div className="flex items-center bg-red-300 px-2 rounded-lg h-12 mr-auto">
                <AlertTriangle className="mr-10" />{" "}
                <div className="font-bold mr-4">Admin:</div>{" "}
                {context.adminEmail}
                <AlertTriangle className="ml-10" />{" "}
              </div>
              <button
                className="hover:cursor-pointer ml-auto"
                onClick={() => {
                  StorageUtility.removeAll();
                  router.invalidate();
                }}
              >
                <LogOut className="mr-2 inline" />
                <div className="">Sign out</div>
              </button>
            </div>
          </div>
          <Outlet />
        </div>
      </main>
    </SidebarProvider>
  );
}
